import React, { useEffect, useRef, useState } from 'react'
import { ProjectPageProps } from './ProjectPage.types'
import styles from './ProjectPage.module.scss'
import cl from 'classnames'
import {
  HorizontalLine, Input, LabeledLine,
  MultiSelect,
  PageHeader, RelatedMediaSection, SectionContainer,
  SwitchButton,
  TagsEditor, Text1, Textarea, VideoSection
} from 'components/shared'
import { IconAttach, IconProjects } from 'assets'
import { Select } from 'components/shared/Select'
import { ProductSection } from './ProductSection'
import { inject } from 'mobx-react'
import {
  BRAND_STORE,
  GENERAL_STORE, PERSON_STORE,
  PROJECT_PAGE_STORE, PROJECT_STORE
} from 'store'
import { observer } from 'mobx-react-lite'
import { ApiVovSubtypes, GenreTypes, Project, ProjectTypes } from 'models'
import { VideoSectionRef } from 'components/shared/VideoSection/VideoSection.types'
import { GalleryImageSection, GalleryImageSectionRef } from './GalleryImageSection'
import { ObjectsPopupSelector } from 'components'
import { PersonsSection } from './PersonsSection'
import { getPosterGalleryTitle, useExitPrompt } from './ProjectPage.utils'
import { SubProjects } from './SubProjects/inedex'
import { Prompt } from 'react-router-dom'
import { formatNumberShort, useGetIsResponsiveScreen } from 'utilities'
import SportPoll from '../SportPoll/SportPoll'

const ProjectPageComponent = (props: ProjectPageProps) => {
  const {
    // [PROJECT_STORE]: {
    //   // clearParentProjectDetails
    // },
    [PROJECT_PAGE_STORE]: {
      // startNewProject,
      project,
      setProjectProps,
      isReadyToSave,
      clearStore,
      projectTypeOptions,
      canAttachVideo,
      seriesOptions,
      seasonOptions,
      triggerVideoProcessing,
      projectHasUnsavedChanges,
      setIfProjectHasUnsavedChanges,
      projectVovSubtypes
    },
    [GENERAL_STORE]: {
      setLoadingOn,
      showError,
      push
    },
    [PERSON_STORE]: {
      personsList,
      getAll
    },
    [BRAND_STORE]: {
      getBrands,
      brandsList,
      getBrandById,
      brandToView
    },
    submit,
    projectName = 'New project'
  } = props

  const setShowExitPrompt = useExitPrompt(projectHasUnsavedChanges)

  useEffect(() => {
    setShowExitPrompt(projectHasUnsavedChanges)
  }, [projectHasUnsavedChanges, setShowExitPrompt])

  useEffect(() => {
    getAll()
    getBrands() // Fetch brands
    return () => clearStore
  }, [clearStore])

  const [popup, setPopup] = useState<'actors' | 'directors' | 'producers'>(null)
  useEffect(() => {
    if (!popup || personsList.length) return
    getAll()
  }, [popup, getAll, personsList.length])
  const videoRef = useRef<VideoSectionRef>(null)
  const postersRef = useRef<GalleryImageSectionRef>(null)
  const [isActiveAutoGroupAccuracy, setIsActiveAutoGroupAccuracy] = useState<boolean>(true) // For Auto Group Accuracy
  const [selectBrandId, setSelectBrandId] = useState<number>(null)
  const { isResponsive: isMobile } = useGetIsResponsiveScreen('(max-width: 920px)')
  const { isResponsive: isLargeLaptop } = useGetIsResponsiveScreen('(max-width: 1400px)')

  const onSubmit = () => {
    // setLoadingOn()

    const {
      handleVideo = async () => ''
    } = videoRef.current || {}

    Promise.all([postersRef.current.handleImages(), handleVideo()])
      .then(([_, videoUrl]) => {
        setProjectProps({
          images: postersRef.current.getImages(),
          videoUrl,
          ...(selectBrandId && { brands: [brandToView] })
        })
        submit()
      })
      .catch(err => showError(err.message))
  }

  const selectType = (type: ProjectTypes) => {
    if (type === ProjectTypes.VOV) {
      setTypeVov(true)
    }
    setProjectProps({ type })
  }

  const [isTypeVov, setTypeVov] = useState(false)

  if (!project) return null

  /**
   * Confirm PIN for editing
   * @returns {boolean} - True if PIN is correct, false otherwise
   */
  function confirmPIN ():boolean {
    const pin = prompt('Enter the PIN:')
    if (pin === '115511') {
      return true
    } else {
      alert('Incorrect PIN. Access denied.')
      return false
    }
  }

  /** If project is in edit mode */
  const isEditMode = localStorage.getItem('readyForEdit')
  const isVideoUnderProcess = localStorage.getItem('manageVideoStatus') === 'PROCESSING' || localStorage.getItem('manageVideoStatus') === 'NOT_FOUND'

  /** Set selected brand on project */
  let filterOptions = []
  if (brandsList?.length > 0) {
    filterOptions = brandsList.map(brand => ({
      label: brand.name,
      value: brand.id
    }))
  }

  const handleDropdownOnSelect = (value:number) => {
    getBrandById(value)
    setSelectBrandId(value)
  }

  return (
    <div className={cl(styles.pageContainer, styles.scrollable)} style={ { overflowX: 'hidden' } }>
      <PageHeader
        navSegments={[
          {
            label: 'Projects',
            icon: IconProjects,
            onClick: () => push('/projects')
          },
          {
            label: projectName
          }
        ]}
        buttons={[{
          children: 'Save',
          preset: 'primary',
          onClick: onSubmit,
          disabled: !isReadyToSave
        }]}
      />

      <SectionContainer className={styles.pageSection}>
        <div className={styles.mainInfoContainer}>
          <div className={styles.mainInfoInner}>
            <LabeledLine
              label='Title'
            >
              <Input
                placeholder='Enter title'
                value={project.name}
                onChange={name => setProjectProps({ name })}
                style={{ width: '100%' }}
              />
            </LabeledLine>
            <LabeledLine
              style={ { overflow: 'visible' } }
              label='Type'
            >
              <Select<ProjectTypes>
                noNoneOption
                placeholder='Choose a type'
                options={projectTypeOptions}
                onChange={type => selectType(type)}
                value={project.type || ''}
                style={{ width: '100%' }}
              />
            </LabeledLine>
            {(isTypeVov || project.type === ProjectTypes.VOV) && (
              <LabeledLine
                label='Subtype'
              >
                <Select<ApiVovSubtypes>
                  noNoneOption
                  placeholder='Choose a type'
                  options={projectVovSubtypes as any}
                  onChange={subtype => setProjectProps({ subtype })}
                  value={project.subtype}
                  style={{ width: '100%' }}
                />
              </LabeledLine>
            )}
            {seriesOptions && (
              <LabeledLine
                label={<><IconAttach style={{ marginRight: 5 }} />Series</>}
              >
                <Select<number>
                  noNoneOption
                  // placeholder='Choose a type'
                  options={seriesOptions}
                  // onChange={type => setProjectProps({ type })}
                  value={seriesOptions[0].value}
                  style={{ width: '100%' }}
                />
              </LabeledLine>
            )}
            {seasonOptions && (
              <LabeledLine
                label={<><IconAttach style={{ marginRight: 5 }} />Season</>}
              >
                <Select<number>
                  noNoneOption
                  // placeholder='Choose a type'
                  options={seasonOptions}
                  // onChange={type => setProjectProps({ type })}
                  value={seasonOptions[0].value}
                  style={{ width: '100%' }}
                />
              </LabeledLine>
            )}
            {project.type === ProjectTypes.TV_EPISODE && (
              <LabeledLine
                label='Episode number'
              >
                <Input
                  placeholder='Enter episode number'
                  type='number'
                  value={project.number}
                  min={0}
                  onChange={number => setProjectProps({ number: Number(number) })}
                />
              </LabeledLine>
            )}
            <LabeledLine
              label='Description'
              alignItems='flexStart'
            >
              <Textarea
                placeholder='Enter a description up to 3000 characters...'
                style={{ minHeight: 170 }}
                value={project.info}
                onChange={info => setProjectProps({ info })}
                maxLength={3000}
              />
            </LabeledLine>
            <LabeledLine
              label='Genres'
            >
              <MultiSelect<GenreTypes>
                placeholder={isMobile ? 'Choose 3 genres' : 'Choose up to 3 genres'}
                options={Object.values(GenreTypes).map(genre => ({
                  label: genre,
                  value: genre
                }))}
                value={project.genres}
                onChange={genres => setProjectProps({ genres })}
                maxSelections={3}
                style={isMobile && { width: '150px' }}
              />

            </LabeledLine>
            {!isLargeLaptop &&
                <LabeledLine
                  label='Year'
                >
                  <Input
                    containerProps={{
                      style: { width: 200 }
                    }}
                    placeholder='YYYY'
                    type='number'
                    min={1900}
                    max={new Date().getFullYear() + 10}
                    maxLength={4}
                    onInput={(event: React.FormEvent<HTMLInputElement>) => {
                      const {
                        value,
                        maxLength
                      } = event.currentTarget
                      if (value.length > maxLength) {
                        event.currentTarget.value = value.slice(0, maxLength)
                      }
                    }}
                    value={project.year || ''}
                    onChange={year => setProjectProps({ year: Number(year) })}
                  />
                </LabeledLine>
            }

            {isLargeLaptop &&
              <LabeledLine
                label='Year'
              >
                <Input
                  containerProps={{
                    style: { width: isMobile ? 100 : 200 }
                  }}
                  placeholder='YYYY'
                  type='number'
                  min={1900}
                  max={new Date().getFullYear() + 10}
                  maxLength={4}
                  onInput={(event: React.FormEvent<HTMLInputElement>) => {
                    const {
                      value,
                      maxLength
                    } = event.currentTarget
                    if (value.length > maxLength) {
                      event.currentTarget.value = value.slice(0, maxLength)
                    }
                  }}
                  value={project.year || ''}
                  onChange={year => setProjectProps({ year: Number(year) })}
                />
              </LabeledLine>
            }
            <LabeledLine
              label='Tags'
            >
              <TagsEditor
                editMode
                tags={project.tags}
                onChange={tags => setProjectProps({ tags })}
              />
            </LabeledLine>

            {/* For Auto Group Accuracy */}
            <LabeledLine
              label='Enable Auto Grouping'
            >
              <SwitchButton
                style={{ marginLeft: 12, marginRight: 12 }}
                isOn={isActiveAutoGroupAccuracy || project.autoGroupAccuracy != null}
                disabled={isEditMode === 'true' || isVideoUnderProcess}
                alwaysSync
                onChange={() => {
                  setIsActiveAutoGroupAccuracy(!isActiveAutoGroupAccuracy)
                  setProjectProps({ autoGroupAccuracy: 0.1 })
                }
                }
              />
              {(isActiveAutoGroupAccuracy || project.autoGroupAccuracy != null) &&
              <Input
                placeholder='Enter auto group accuracy'
                value={project?.autoGroupAccuracy || 0.1}
                type='number'
                min={0}
                step={0.1}
                max={1}
                disabled={isEditMode === 'true' || isVideoUnderProcess}
                onChange={autoGroupAccuracy => {
                  if (isEditMode === 'false' && window.location.pathname.includes('edit')) {
                    const isPinCorrect = confirmPIN()
                    if (isPinCorrect) {
                      setProjectProps({ autoGroupAccuracy: +(autoGroupAccuracy) })
                      return
                    } else {
                      return
                    }
                  }
                  setProjectProps({ autoGroupAccuracy: +(autoGroupAccuracy) })
                } }
              />}
            </LabeledLine>
            {(isActiveAutoGroupAccuracy || project.autoGroupAccuracy != null) &&
            <p
              className={styles.infoText}
            >
            *This setting controls the accuracy of the auto-grouping algorithm.
            By default, it&apos;s set to 0.1 <br/> (Note: 0.1 = most accurate, 0 = identical, 1 = different.)
            </p>
            }
            <LabeledLine
              label='Stores'
            >
              <Select
                className={styles.dropDown}
                options={filterOptions}
                value={project?.brands?.length > 0 ? project?.brands[0]?.id : selectBrandId}
                onChange={handleDropdownOnSelect}
                noNoneOption
                placeholder='Select store'
              />
            </LabeledLine>
          </div>
          <div className={styles.mainInfoInner}>
            <div className={styles.tokenUsed}>
              <div className={styles.tokenUsedItem}>
                <Text1 bold>Input Token Used</Text1>
                <Text1 className={styles.tokenValue}>{formatNumberShort(project.inputGeminiTokens || 0)}</Text1>
              </div>
              <div className={styles.tokenUsedItem}>
                <Text1 bold>Output Token Used</Text1>
                <Text1 className={styles.tokenValue}>{formatNumberShort(project.outputGeminiTokens || 0)}</Text1>
              </div>
            </div>

            {canAttachVideo && (
              <VideoSection
                ref={videoRef}
                videoUrl={project.videoUrl}
                readyForEdit={project.readyForEdit}
                openInEditor={() => push(`/editor/${project.id}`)}
                triggerNewVideoProcessing={triggerVideoProcessing}
                onChange={() => setIfProjectHasUnsavedChanges(true)}
              />
            )}
          </div>

        </div>
        <HorizontalLine />

        <GalleryImageSection
          title={getPosterGalleryTitle(project.type)}
          initialImages={project.images}
          ref={postersRef}
          onChange={() => setIfProjectHasUnsavedChanges(true)}
        />

        <PersonsSection
          personType='actors'
          list={project.actors}
          onAddButtonClick={() => setPopup('actors')}
          onRemovePerson={id => setProjectProps({
            actors: project.actors.filter(p => p.id !== id)
          })}
        />

        <PersonsSection
          personType='directors'
          list={project.directors}
          onAddButtonClick={() => setPopup('directors')}
          onRemovePerson={id => setProjectProps({
            directors: project.directors.filter(p => p.id !== id)
          })}
        />

        <PersonsSection
          personType='producers'
          list={project.producers}
          onAddButtonClick={() => setPopup('producers')}
          onRemovePerson={id => setProjectProps({
            producers: project.producers.filter(p => p.id !== id)
          })}
        />

        <SubProjects />

      </SectionContainer>

      <RelatedMediaSection
        relatedMedia={project.relatedMedia as unknown as Project[]}
        onChange={(relatedMedia: any[]) => setProjectProps({ relatedMedia })}
      />

      <ProductSection
        className={styles.pageSection}
      />

      {
        project.type === ProjectTypes.SPORT &&
        <SportPoll />
      }

      {popup && (
        <ObjectsPopupSelector
          title={`manage ${popup} of your project`}
          onClose={() => setPopup(null)}
          objectsList={personsList}
          selectedObjectsList={project[popup]}
          setSelectedList={list => setProjectProps({ [popup]: list })}
        />
      )}

      <Prompt
        when={projectHasUnsavedChanges}
        message='Are you sure you want to leave? Unsaved changes will be lost.'
      />
    </div>
  )
}

const ProjectPage = inject(
  PROJECT_STORE,
  PROJECT_PAGE_STORE,
  GENERAL_STORE,
  PERSON_STORE,
  BRAND_STORE
)(observer(ProjectPageComponent))
export { ProjectPage }
